<template>
  <Dialog
    v-model:visible="isVisible"
    header="Charge now"
    modal
    :style="{ width: '28rem' }"
    :closable="!isLoading"
  >
    <p class="text-gray-600 mb-4">Please review the payment details below:</p>

    <div class="border border-gray-200 rounded-lg p-4">
      <div class="grid grid-cols-[auto_1fr] gap-x-8 gap-y-3">
        <div class="font-semibold text-gray-900">Paid by</div>
        <div class="text-gray-700">{{ payment.paid_by || "N/A" }}</div>

        <div class="font-semibold text-gray-900">Amount</div>
        <div class="text-gray-700">{{ formattedAmount }}</div>

        <div class="font-semibold text-gray-900">Method</div>
        <div class="text-gray-700">{{ formattedMethod }}</div>

        <div class="font-semibold text-gray-900">Source</div>
        <div class="text-gray-700">{{ payment.source_name || "N/A" }}</div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-4">
        <Button
          label="Go back"
          text
          severity="secondary"
          :disabled="isLoading"
          @click="handleClose"
        />
        <Button
          label="Complete Payment"
          :loading="isLoading"
          @click="handleCharge"
        />
      </div>
    </template>
  </Dialog>

  <!-- Error Dialog - Payment Declined -->
  <Dialog
    v-model:visible="showErrorDialog"
    modal
    :closable="true"
    :style="{ width: '28rem' }"
  >
    <template #header>
      <div class="flex items-center gap-2 text-red-600">
        <i class="pi pi-exclamation-circle text-xl" />
        <span class="font-semibold">Payment failed</span>
      </div>
    </template>

    <p class="text-gray-700 mb-4">
      {{ declinedErrorMessage }}
    </p>

    <p v-if="canAddPaymentMethod" class="text-gray-600">
      You can also
      <a
        href="#"
        class="text-blue-600 hover:text-blue-800 underline cursor-pointer"
        @click.prevent="navigateToAddPaymentMethod"
      >
        add a new payment method
      </a>
      and then try again.
    </p>

    <template #footer>
      <div class="flex justify-end">
        <Button label="Close" @click="showErrorDialog = false" />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouterNavigation } from "@/composables/useRouterNavigation";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";
import { useChargePayment } from "../composables/useChargePayment";
import { getFormattedCurrency } from "@/shared/utils/helpers";
import {
  formatPaymentMethod,
  getPaymentSourceRoute,
} from "@/modules/payments/helpers";
import type { PaymentsLog, PaymentsLogDetail } from "@/api/model";

interface Props {
  payment: PaymentsLog | PaymentsLogDetail;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  success: [];
}>();

const isVisible = defineModel<boolean>("visible", { required: true });

const { navigateWithQuery } = useRouterNavigation();
const toast = useToast();
const { isLoading, chargePayment } = useChargePayment();

const showErrorDialog = ref(false);
const backendErrorMessage = ref<string | null>(null);
const isCardExpired = ref(false);

const formattedAmount = computed(() => {
  return getFormattedCurrency(props.payment.amount_with_fees);
});

const formattedMethod = computed(() => {
  return formatPaymentMethod(props.payment.payment_method);
});

const declinedErrorMessage = computed(() => {
  const method = formattedMethod.value;
  // An expired card is a decline we can name, so say so instead of asking the
  // admin to re-check details that are not wrong (MAI-5001).
  if (isCardExpired.value) {
    return `The payment method (${method}) has expired. Ask the payer to update their card, or add a new payment method.`;
  }
  // Use backend error message if available, otherwise show generic message
  if (backendErrorMessage.value) {
    return backendErrorMessage.value;
  }
  return `The payment method (${method}) was declined. Please verify that the payment information is correct and that there are sufficient funds in the account.`;
});

const canAddPaymentMethod = computed(() => {
  const sourceType = props.payment.source_type;
  return (
    sourceType === "subscriber_membership" ||
    sourceType === "subscriber_registration"
  );
});

const navigateToAddPaymentMethod = () => {
  showErrorDialog.value = false;
  isVisible.value = false;

  const route = getPaymentSourceRoute(props.payment);
  if (route) {
    navigateWithQuery(route);
  }
};

const handleClose = () => {
  isVisible.value = false;
};

const handleCharge = async () => {
  const result = await chargePayment(props.payment.id);

  if (result.success) {
    const statusMessage =
      result.data?.status === "PROCESSING"
        ? "Payment is being processed"
        : "Payment has been processed successfully";

    toast.add({
      severity: "success",
      summary: "Success",
      detail: statusMessage,
      life: 3000,
    });
    isVisible.value = false;
    emit("success");
    return;
  }

  // Handle different error types
  switch (result.errorType) {
    // Both are declines and use the same dialog -- which already offers "add a new
    // payment method", the right next step for an expired card.
    case "card_expired":
    case "payment_declined":
      isCardExpired.value = result.errorType === "card_expired";
      backendErrorMessage.value = result.errorMessage || null;
      showErrorDialog.value = true;
      break;

    case "validation_error":
      toast.add({
        severity: "warn",
        summary: "Cannot Charge",
        detail: result.errorMessage || "This payment cannot be charged",
        life: 5000,
      });
      isVisible.value = false;
      break;

    case "permission_denied":
      toast.add({
        severity: "error",
        summary: "Permission Denied",
        detail: "You don't have permission to charge payments",
        life: 5000,
      });
      isVisible.value = false;
      break;

    case "not_found":
      toast.add({
        severity: "error",
        summary: "Not Found",
        detail: "Payment not found. It may have been deleted.",
        life: 5000,
      });
      isVisible.value = false;
      emit("success"); // Refresh to remove stale payment
      break;

    case "retry_limit":
      toast.add({
        severity: "warn",
        summary: "Retry Limit Reached",
        detail: "Maximum retry attempts reached. Please try again later.",
        life: 5000,
      });
      isVisible.value = false;
      emit("success"); // Refresh to update UI (hide charge now action)
      break;

    default:
      toast.add({
        severity: "error",
        summary: "Error",
        detail: result.errorMessage || "Failed to process payment",
        life: 5000,
      });
  }
};
</script>
